'use client';

import { useState, useEffect, useCallback } from 'react';
import { ActivityEvent, getActivityEvents, generateActivityEvent, addActivityEvent, seedInitialActivity } from '@/lib/bookingActivityFeed';

const COLOR_CLASSES: Record<string, { bg: string; dot: string }> = {
  green: { bg: 'bg-green-50', dot: 'bg-green-500' },
  blue: { bg: 'bg-blue-50', dot: 'bg-blue-500' },
  red: { bg: 'bg-red-50', dot: 'bg-red-500' },
  purple: { bg: 'bg-purple-50', dot: 'bg-purple-500' },
  orange: { bg: 'bg-orange-50', dot: 'bg-orange-500' },
  yellow: { bg: 'bg-yellow-50', dot: 'bg-yellow-500' },
  indigo: { bg: 'bg-indigo-50', dot: 'bg-indigo-500' },
  gray: { bg: 'bg-gray-50', dot: 'bg-gray-400' },
};

function timeAgo(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function LiveActivityFeedAdmin() {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [isLive, setIsLive] = useState(true);
  const [filter, setFilter] = useState<string>('all');

  const loadEvents = useCallback(() => { seedInitialActivity(); setEvents(getActivityEvents()); }, []);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  useEffect(() => {
    if (!isLive) return;
    const interval = setInterval(() => {
      const newEvent = generateActivityEvent();
      addActivityEvent(newEvent);
      setEvents(getActivityEvents());
    }, 6000 + Math.random() * 6000);
    return () => clearInterval(interval);
  }, [isLive]);

  const filterOptions = [
    { id: 'all', label: 'All' },
    { id: 'booking', label: 'Bookings' },
    { id: 'payment', label: 'Payments' },
    { id: 'support', label: 'Support' },
    { id: 'lead', label: 'Leads' },
  ];

  const filtered = events.filter(e => {
    if (filter === 'all') return true;
    if (filter === 'booking') return ['booking_created', 'booking_confirmed', 'booking_cancelled', 'ticket_issued'].includes(e.type);
    if (filter === 'payment') return e.type === 'payment_received';
    if (filter === 'support') return ['support_ticket_created', 'support_ticket_resolved'].includes(e.type);
    if (filter === 'lead') return e.type === 'new_lead';
    return true;
  }).slice(0, 20);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <div className="w-2.5 h-2.5 bg-green-500 rounded-full"></div>
            {isLive && <div className="absolute inset-0 w-2.5 h-2.5 bg-green-500 rounded-full animate-ping opacity-75"></div>}
          </div>
          <span className="font-bold text-foreground">Live Activity Feed</span>
          <span className="text-xs text-muted-foreground">({events.length} events)</span>
        </div>
        <button onClick={() => setIsLive(p => !p)} className={`text-xs px-3 py-1.5 rounded-lg font-bold border transition-all ${
          isLive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-600 border-gray-200'
        }`}>
          {isLive ? 'Pause' : 'Resume'}
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {filterOptions.map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
            filter === f.id ? 'bg-primary text-white' : 'bg-surface text-muted-foreground hover:text-foreground border border-border'
          }`}>{f.label}</button>
        ))}
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
        {filtered.map((event, idx) => {
          const colors = COLOR_CLASSES[event.color] || COLOR_CLASSES.gray;
          return (
            <div key={event.id} className={`flex items-start gap-3 p-3 rounded-xl border transition-all duration-300 ${colors.bg} ${
              idx === 0 && event.isNew ? 'ring-1 ring-primary/30' : 'border-transparent'
            }`}>
              <div className="flex-shrink-0 mt-0.5">
                <div className={`w-2 h-2 rounded-full ${colors.dot}`}></div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-bold text-foreground">{event.icon} {event.title}</span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{timeAgo(event.timestamp)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{event.description}</p>
                {event.bookingRef && <span className="text-xs font-mono text-muted-foreground opacity-60">#{event.bookingRef}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
